import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './Envase.css';

const formatearFecha = (f) => {
    if (!f) return '';
    const [y, m, d] = String(f).split('T')[0].split('-');
    return `${d}/${m}/${y}`;
};

const obtenerFechaHoy = () => new Date().toISOString().split('T')[0];

const formatearNumero = (v, dec = 2) => {
    if (v == null || v === '') return '0,00';
    return Number(v).toLocaleString('es-PY', {
        minimumFractionDigits: dec, maximumFractionDigits: dec
    });
};

const formularioInicial = () => ({
    idSucursal: 1,
    boca: 1,
    fecha: obtenerFechaHoy(),
    idProveedor: '',
    nomProveedor: '',
    rucProveedor: '',
    idDeposito: '',
    remitoProv: '',
    fechaRemito: '',
    nomTransportista: '',
    rucTransportista: '',
    marcaVehiculo: '',
    chapa: '',
    nomConductor: '',
    ciConductor: '',
    idMotivo: 'REC',
    otroMotivo: '',
    obs: '',
    ordencompra: '',
    detalles: []
});

const chipEstado = (estado) => {
    const map = {
        'A': { txt: 'Activa',  bg: '#E4F2FC', color: '#004F9F', ico: '🟢' },
        'C': { txt: 'Cerrada', bg: '#E8F5E9', color: '#2E7D32', ico: '🔒' },
        'N': { txt: 'Anulada', bg: '#FFEBEE', color: '#C62828', ico: '🚫' }
    };
    const s = map[estado] || map.A;
    return (
        <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: '12px',
            backgroundColor: s.bg, color: s.color, fontSize: '11px', fontWeight: '600'
        }}>{s.ico} {s.txt}</span>
    );
};

function SRecepMateriales({ usuario }) {
    const [recepciones, setRecepciones] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [fechaDesde, setFechaDesde] = useState('');
    const [fechaHasta, setFechaHasta] = useState('');
    const [pagina, setPagina] = useState(1);
    const [porPagina, setPorPagina] = useState(10);

    const [modalAbierto, setModalAbierto] = useState(false);
    const [modalVerAbierto, setModalVerAbierto] = useState(false);
    const [recepVer, setRecepVer] = useState(null);
    const [formulario, setFormulario] = useState(formularioInicial());

    const [depositos, setDepositos] = useState([]);
    const [motivos, setMotivos] = useState([]);

    const [busquedaProv, setBusquedaProv] = useState('');
    const [provsEncontrados, setProvsEncontrados] = useState([]);

    const [busquedaProducto, setBusquedaProducto] = useState('');
    const [productosEncontrados, setProductosEncontrados] = useState([]);
    const [detalleTemp, setDetalleTemp] = useState({
        idProducto: '', nomProducto: '', cantidad: 1, precio: 0, idMedida: null
    });

    // ---------- Carga inicial ----------
    useEffect(() => {
        cargar();
        cargarDepositos();
        cargarMotivos();
    }, []);

    const cargar = async () => {
        try {
            const { data } = await axios.get('/api/recepmateriales');
            setRecepciones(data);
        } catch (e) {
            console.error(e);
        }
    };
    const cargarDepositos = async () => {
        try {
            const { data } = await axios.get('/api/recepmateriales/depositos');
            setDepositos(data);
        } catch (e) { console.error(e); }
    };
    const cargarMotivos = async () => {
        try {
            const { data } = await axios.get('/api/recepmateriales/motivos');
            setMotivos(data);
        } catch (e) { console.error(e); }
    };

    // ---------- Filtro / paginacion ----------
    const filtradas = recepciones.filter(r => {
        const b = busqueda.toLowerCase();
        const okBusqueda = !b || String(r.nrecep).includes(b)
            || String(r.nom_proveedor || '').toLowerCase().includes(b)
            || String(r.remito_prov || '').toLowerCase().includes(b);
        const okDesde = !fechaDesde || (r.fecha && String(r.fecha) >= fechaDesde);
        const okHasta = !fechaHasta || (r.fecha && String(r.fecha) <= fechaHasta);
        return okBusqueda && okDesde && okHasta;
    });
    const totalPaginas = Math.max(1, Math.ceil(filtradas.length / porPagina));
    const paginaActual = Math.min(pagina, totalPaginas);
    const paginadas = filtradas.slice((paginaActual - 1) * porPagina, paginaActual * porPagina);

    // ---------- Export excel ----------
    const exportar = () => {
        const filas = filtradas.map(r => ({
            'Nro': `${r.id_sucursal}-${r.boca}-${r.nrecep}`,
            'Fecha': formatearFecha(r.fecha),
            'Proveedor': r.nom_proveedor,
            'RUC': r.ruc_proveedor,
            'Deposito': r.nom_deposito || r.id_deposito,
            'Remito Prov.': r.remito_prov || '',
            'F. Remito': r.fecha_remito ? formatearFecha(r.fecha_remito) : '',
            'Orden Compra': r.ordencompra || '',
            'Factura Vinculada': r.ff_nfactura ? `${r.ff_tipo_doc}-${r.ff_nfactura}` : '',
            'Estado': r.estado
        }));
        const ws = XLSX.utils.json_to_sheet(filas);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Recepciones');
        XLSX.writeFile(wb, `recepciones_${obtenerFechaHoy()}.xlsx`);
    };

    // ---------- Alta ----------
    const abrirModal = () => {
        setFormulario(formularioInicial());
        setBusquedaProv(''); setProvsEncontrados([]);
        setBusquedaProducto(''); setProductosEncontrados([]);
        setDetalleTemp({ idProducto: '', nomProducto: '', cantidad: 1, precio: 0, idMedida: null });
        setModalAbierto(true);
    };
    const cerrarModal = () => setModalAbierto(false);

    // Proveedor
    useEffect(() => {
        if (busquedaProv.length < 2) { setProvsEncontrados([]); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await axios.get('/api/recepmateriales/buscar-proveedor', { params: { busqueda: busquedaProv } });
                setProvsEncontrados(data);
            } catch (e) { console.error(e); }
        }, 250);
        return () => clearTimeout(t);
    }, [busquedaProv]);

    const seleccionarProveedor = (p) => {
        setFormulario(f => ({
            ...f,
            idProveedor: p.id_proveedor,
            nomProveedor: p.nom_proveedor,
            rucProveedor: p.ruc_prov
        }));
        setBusquedaProv(p.nom_proveedor);
        setProvsEncontrados([]);
    };

    // Producto
    useEffect(() => {
        if (busquedaProducto.length < 2) { setProductosEncontrados([]); return; }
        let vigente = true;   // false si el texto cambio antes de llegar la respuesta
        const t = setTimeout(async () => {
            try {
                const { data } = await axios.get('/api/recepmateriales/buscar-producto', { params: { busqueda: busquedaProducto } });
                if (vigente) setProductosEncontrados(data);
            } catch (e) { console.error(e); }
        }, 250);
        return () => { vigente = false; clearTimeout(t); };
    }, [busquedaProducto]);

    // Al hacer clic en un resultado -> cargar al editor de linea (NO agregar aun)
    const cargarEnEditor = (p) => {
        setDetalleTemp({
            idProducto:  String(p.id_producto).trim(),
            nomProducto: p.descripcion,
            cantidad:    1,
            precio:      Number(p.costoulcom || p.costoprom || 0),
            idMedida:    p.id_medida || null
        });
        setBusquedaProducto('');
        setProductosEncontrados([]);
    };

    // Buscar producto por codigo exacto y cargarlo al editor
    const buscarPorCodigo = async (codigo) => {
        const cod = String(codigo).trim();
        if (!cod) return;
        try {
            const { data } = await axios.get('/api/recepmateriales/buscar-producto', {
                params: { busqueda: cod }
            });
            const exact = data.find(p => String(p.id_producto).trim() === cod);
            if (exact) {
                cargarEnEditor(exact);
            } else if (data.length === 1) {
                cargarEnEditor(data[0]);
            } else if (data.length > 1) {
                setProductosEncontrados(data);
            } else {
                alert('No se encontro ningun articulo con ese criterio');
            }
        } catch (e) {
            console.error(e);
            alert('Error al buscar articulo');
        }
    };

    // Boton "+ Agregar" -> mueve el editor al array de detalles
    const agregarLinea = () => {
        if (!detalleTemp.idProducto) {
            alert('Seleccione un articulo primero');
            return;
        }
        if (!(Number(detalleTemp.cantidad) > 0)) {
            alert('La cantidad debe ser mayor a 0');
            return;
        }
        if (formulario.detalles.find(d => d.idProducto === detalleTemp.idProducto)) {
            alert('Ese articulo ya esta en el detalle');
            return;
        }
        setFormulario(f => ({
            ...f,
            detalles: [...f.detalles, {
                idProducto:  detalleTemp.idProducto,
                nomProducto: detalleTemp.nomProducto,
                cantidad:    Number(detalleTemp.cantidad),
                precio:      Number(detalleTemp.precio),
                idMedida:    detalleTemp.idMedida
            }]
        }));
        setDetalleTemp({ idProducto: '', nomProducto: '', cantidad: 1, precio: 0, idMedida: null });
    };

    const actualizarDetalle = (idProducto, campo, valor) => {
        setFormulario(f => ({
            ...f,
            detalles: f.detalles.map(d =>
                d.idProducto === idProducto ? { ...d, [campo]: Number(valor) || 0 } : d
            )
        }));
    };
    const quitarDetalle = (idProducto) => {
        setFormulario(f => ({ ...f, detalles: f.detalles.filter(d => d.idProducto !== idProducto) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formulario.idProveedor)  return alert('Seleccione un proveedor');
        if (!formulario.idDeposito)   return alert('Seleccione un deposito');
        if (!formulario.fecha)        return alert('Fecha obligatoria');
        if (formulario.detalles.length === 0) return alert('Agregue al menos un articulo');
        for (const d of formulario.detalles) {
            if (!(Number(d.cantidad) > 0))
                return alert(`Cantidad > 0 para ${d.nomProducto}`);
        }
        try {
            await axios.post('/api/recepmateriales', formulario);
            alert('Recepcion registrada. Stock actualizado.');
            cerrarModal();
            cargar();
        } catch (err) {
            alert(err.response?.data?.message || 'Error al guardar');
        }
    };

    // ---------- Ver / Anular ----------
    const abrirVer = async (r) => {
        try {
            const { data } = await axios.get(
                `/api/recepmateriales/${r.id_sucursal}/${r.boca}/${r.nrecep}`);
            setRecepVer(data);
            setModalVerAbierto(true);
        } catch (e) { console.error(e); alert('Error al cargar detalle'); }
    };

    const anular = async (r) => {
        if (!window.confirm(`Anular recepcion #${r.nrecep}? El stock sera revertido.`)) return;
        try {
            await axios.put(`/api/recepmateriales/${r.id_sucursal}/${r.boca}/${r.nrecep}/anular`);
            alert('Recepcion anulada. Stock revertido.');
            cargar();
        } catch (e) {
            alert(e.response?.data?.message || 'Error al anular');
        }
    };

    const reactivar = async (r) => {
        if (!window.confirm(`Reactivar recepcion #${r.nrecep}? El stock sera sumado nuevamente.`)) return;
        try {
            await axios.put(`/api/recepmateriales/${r.id_sucursal}/${r.boca}/${r.nrecep}/reactivar`);
            alert('Recepcion reactivada.');
            cargar();
        } catch (e) {
            alert(e.response?.data?.message || 'Error al reactivar');
        }
    };

    // ---------- Render ----------
    return (
        <div style={{ padding: '20px', fontFamily: 'Gilam, sans-serif' }}>
            <h2 style={{ color: '#004F9F', marginBottom: '20px' }}>Recepcion de Materiales</h2>

            <div style={{
                display: 'flex', gap: '10px', marginBottom: '20px',
                flexWrap: 'wrap', alignItems: 'center'
            }}>
                <input type="text" placeholder="Buscar (nro / proveedor / remito)"
                    value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                    style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '260px' }} />
                <label>Desde:
                    <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)}
                        style={{ marginLeft: '5px', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                </label>
                <label>Hasta:
                    <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)}
                        style={{ marginLeft: '5px', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                </label>
                <button onClick={abrirModal} style={{
                    padding: '8px 16px', background: '#004F9F', color: 'white',
                    border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600'
                }}>+ Nueva Recepcion</button>
                <button onClick={exportar} style={{
                    padding: '8px 16px', background: '#4CAF50', color: 'white',
                    border: 'none', borderRadius: '4px', cursor: 'pointer'
                }}>Exportar Excel</button>
            </div>

            <div style={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#004F9F', color: 'white' }}>
                        <tr>
                            <th style={{ padding: '10px', textAlign: 'left' }}>Nro</th>
                            <th style={{ padding: '10px', textAlign: 'left' }}>Fecha</th>
                            <th style={{ padding: '10px', textAlign: 'left' }}>Proveedor</th>
                            <th style={{ padding: '10px', textAlign: 'left' }}>Deposito</th>
                            <th style={{ padding: '10px', textAlign: 'left' }}>Remito Prov.</th>
                            <th style={{ padding: '10px', textAlign: 'center' }}>OC</th>
                            <th style={{ padding: '10px', textAlign: 'center' }}>Factura</th>
                            <th style={{ padding: '10px', textAlign: 'center' }}>Estado</th>
                            <th style={{ padding: '10px', textAlign: 'center' }}>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginadas.map(r => (
                            <tr key={`${r.id_sucursal}-${r.boca}-${r.nrecep}`}
                                style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '8px' }}>
                                    <strong>#{r.nrecep}</strong>
                                    <span style={{ color: '#888', fontSize: '11px' }}> · {r.id_sucursal}-{r.boca}</span>
                                </td>
                                <td style={{ padding: '8px' }}>{formatearFecha(r.fecha)}</td>
                                <td style={{ padding: '8px' }}>{r.nom_proveedor}</td>
                                <td style={{ padding: '8px' }}>{r.nom_deposito || r.id_deposito}</td>
                                <td style={{ padding: '8px' }}>{r.remito_prov || '-'}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                    {r.ordencompra ? <span style={{
                                        padding: '2px 6px', borderRadius: '8px',
                                        background: '#FFF3E0', color: '#E65100', fontSize: '11px'
                                    }}>#{r.ordencompra}</span> : '-'}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                    {r.ff_nfactura ? <span style={{
                                        padding: '2px 6px', borderRadius: '8px',
                                        background: '#E8F5E9', color: '#2E7D32', fontSize: '11px'
                                    }}>{r.ff_tipo_doc}-{r.ff_nfactura}</span> : '-'}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>{chipEstado(r.estado)}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                    <button onClick={() => abrirVer(r)} title="Ver" style={{
                                        marginRight: '4px', background: 'none', border: '1px solid #ccc',
                                        borderRadius: '4px', padding: '3px 8px', cursor: 'pointer'
                                    }}>👁</button>
                                    {r.estado === 'A' && (
                                        <button onClick={() => anular(r)} title="Anular (revierte stock)" style={{
                                            background: 'none', border: '1px solid #C62828',
                                            color: '#C62828', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer'
                                        }}>🚫</button>
                                    )}
                                    {r.estado === 'N' && (
                                        <button onClick={() => reactivar(r)} title="Reactivar" style={{
                                            background: 'none', border: '1px solid #2E7D32',
                                            color: '#2E7D32', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer'
                                        }}>♻</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {paginadas.length === 0 && (
                            <tr><td colSpan="9" style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                                Sin recepciones
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginacion */}
            <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button disabled={paginaActual === 1} onClick={() => setPagina(p => p - 1)}>‹</button>
                <span>Pagina {paginaActual} de {totalPaginas}</span>
                <button disabled={paginaActual === totalPaginas} onClick={() => setPagina(p => p + 1)}>›</button>
                <select value={porPagina} onChange={(e) => { setPorPagina(Number(e.target.value)); setPagina(1); }}>
                    {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / pag</option>)}
                </select>
                <span style={{ marginLeft: '10px', color: '#666' }}>{filtradas.length} recepcion(es)</span>
            </div>

            {/* -------- Modal ALTA -------- */}
            {modalAbierto && (
                <div className="atm-modal-overlay" style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)', display: 'flex',
                    justifyContent: 'center', alignItems: 'flex-start', padding: '20px',
                    zIndex: 1000, overflowY: 'auto'
                }}>
                    <div style={{
                        background: 'white', borderRadius: '8px', padding: '20px',
                        width: '95%', maxWidth: '1100px'
                    }}>
                        <h3 style={{ color: '#004F9F', marginTop: 0 }}>Nueva Recepcion de Materiales</h3>
                        <form onSubmit={handleSubmit}>
                            {/* Cabecera */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                                <div>
                                    <label>Fecha *</label>
                                    <input type="date" value={formulario.fecha} required
                                        onChange={(e) => setFormulario(f => ({ ...f, fecha: e.target.value }))}
                                        style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                </div>
                                <div>
                                    <label>Deposito *</label>
                                    <select value={formulario.idDeposito} required
                                        onChange={(e) => setFormulario(f => ({ ...f, idDeposito: e.target.value }))}
                                        style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}>
                                        <option value="">-- Seleccione --</option>
                                        {depositos.map(d => (
                                            <option key={d.id_deposito} value={d.id_deposito}>
                                                {d.id_deposito} - {d.nom_depto}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label>Motivo</label>
                                    <select value={formulario.idMotivo}
                                        onChange={(e) => setFormulario(f => ({ ...f, idMotivo: e.target.value }))}
                                        style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}>
                                        {motivos.map(m => (
                                            <option key={m.id_motivo} value={m.id_motivo}>{m.descripcion}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label>OC Vinculada (opc.)</label>
                                    <input type="number" value={formulario.ordencompra}
                                        onChange={(e) => setFormulario(f => ({ ...f, ordencompra: e.target.value }))}
                                        style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                </div>
                            </div>

                            {/* Proveedor */}
                            <div style={{ marginTop: '10px', position: 'relative' }}>
                                <label>Proveedor *</label>
                                <input type="text" placeholder="Buscar por nombre / RUC / codigo"
                                    value={busquedaProv} onChange={(e) => setBusquedaProv(e.target.value)}
                                    style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                {provsEncontrados.length > 0 && (
                                    <div style={{
                                        position: 'absolute', top: '100%', left: 0, right: 0,
                                        background: 'white', border: '1px solid #ddd', borderRadius: '4px',
                                        maxHeight: '160px', overflowY: 'auto', zIndex: 10
                                    }}>
                                        {provsEncontrados.map(p => (
                                            <div key={p.id_proveedor} onClick={() => seleccionarProveedor(p)}
                                                style={{ padding: '6px', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
                                                <strong>{p.nom_proveedor}</strong>
                                                <span style={{ color: '#666', marginLeft: '10px' }}>RUC {p.ruc_prov}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {formulario.idProveedor && (
                                    <div style={{ marginTop: '4px', color: '#004F9F' }}>
                                        <strong>{formulario.nomProveedor}</strong> · RUC {formulario.rucProveedor}
                                    </div>
                                )}
                            </div>

                            {/* Remito del proveedor */}
                            <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                                <div>
                                    <label>Nro Remito Prov.</label>
                                    <input type="text" value={formulario.remitoProv}
                                        onChange={(e) => setFormulario(f => ({ ...f, remitoProv: e.target.value }))}
                                        style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                </div>
                                <div>
                                    <label>Fecha Remito</label>
                                    <input type="date" value={formulario.fechaRemito}
                                        onChange={(e) => setFormulario(f => ({ ...f, fechaRemito: e.target.value }))}
                                        style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                </div>
                                <div>
                                    <label>Chapa Vehiculo</label>
                                    <input type="text" maxLength={7} value={formulario.chapa}
                                        onChange={(e) => setFormulario(f => ({ ...f, chapa: e.target.value.toUpperCase() }))}
                                        style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                </div>
                                <div>
                                    <label>Marca Vehiculo</label>
                                    <input type="text" value={formulario.marcaVehiculo}
                                        onChange={(e) => setFormulario(f => ({ ...f, marcaVehiculo: e.target.value }))}
                                        style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                </div>
                                <div>
                                    <label>Transportista</label>
                                    <input type="text" value={formulario.nomTransportista}
                                        onChange={(e) => setFormulario(f => ({ ...f, nomTransportista: e.target.value }))}
                                        style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                </div>
                                <div>
                                    <label>RUC Transportista</label>
                                    <input type="text" value={formulario.rucTransportista}
                                        onChange={(e) => setFormulario(f => ({ ...f, rucTransportista: e.target.value }))}
                                        style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                </div>
                                <div>
                                    <label>Conductor</label>
                                    <input type="text" value={formulario.nomConductor}
                                        onChange={(e) => setFormulario(f => ({ ...f, nomConductor: e.target.value }))}
                                        style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                </div>
                                <div>
                                    <label>CI Conductor</label>
                                    <input type="text" value={formulario.ciConductor}
                                        onChange={(e) => setFormulario(f => ({ ...f, ciConductor: e.target.value }))}
                                        style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                </div>
                            </div>

                            <div style={{ marginTop: '10px' }}>
                                <label>Observaciones</label>
                                <input type="text" value={formulario.obs}
                                    onChange={(e) => setFormulario(f => ({ ...f, obs: e.target.value }))}
                                    style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                            </div>

                            {/* Detalle */}
                            <div style={{ marginTop: '18px', padding: '10px',
                                background: '#F5F9FE', borderRadius: '6px' }}>
                                <h4 style={{ margin: '0 0 8px 0', color: '#004F9F' }}>Articulos recibidos</h4>

                                {/* Buscador con dropdown */}
                                <div style={{ position: 'relative', marginBottom: '10px' }}>
                                    <label style={{ fontSize: '12px', color: '#555' }}>
                                        Buscar articulo (escriba descripcion o codigo, o pegue el codigo y presione Enter)
                                    </label>
                                    <input type="text" placeholder="ej: 9801 o 'baterias'"
                                        value={busquedaProducto}
                                        onChange={(e) => setBusquedaProducto(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') { e.preventDefault(); buscarPorCodigo(busquedaProducto); }
                                        }}
                                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd',
                                                 borderRadius: '4px', fontSize: '14px' }} />
                                    {productosEncontrados.length > 0 && (
                                        <div style={{
                                            position: 'absolute', top: '100%', left: 0, right: 0,
                                            background: 'white', border: '1px solid #004F9F', borderRadius: '4px',
                                            maxHeight: '240px', overflowY: 'auto', zIndex: 100,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                        }}>
                                            {productosEncontrados.map(p => (
                                                <div key={p.id_producto} onClick={() => cargarEnEditor(p)}
                                                    style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E4F2FC'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}>
                                                    <strong>{String(p.id_producto).trim()}</strong> · {p.descripcion}
                                                    <span style={{ color: '#888', marginLeft: '10px', fontSize: '12px' }}>
                                                        stock: {formatearNumero(p.existencia)} · ult.compra: {formatearNumero(p.costoulcom)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Editor de linea (activo cuando hay producto seleccionado) */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '110px 1fr 110px 130px 90px',
                                    gap: '8px', alignItems: 'end',
                                    padding: '10px',
                                    background: detalleTemp.idProducto ? '#FFF8E1' : '#EEEEEE',
                                    border: detalleTemp.idProducto ? '1px solid #FFB300' : '1px dashed #BBB',
                                    borderRadius: '4px', marginBottom: '10px'
                                }}>
                                    <div>
                                        <label style={{ fontSize: '11px', color: '#555' }}>Codigo</label>
                                        <input type="text" readOnly value={detalleTemp.idProducto}
                                            placeholder="—"
                                            style={{ width: '100%', padding: '6px', border: '1px solid #ddd',
                                                     borderRadius: '4px', background: '#fff' }} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '11px', color: '#555' }}>Descripcion</label>
                                        <input type="text" readOnly value={detalleTemp.nomProducto}
                                            placeholder="Seleccione un articulo arriba"
                                            style={{ width: '100%', padding: '6px', border: '1px solid #ddd',
                                                     borderRadius: '4px', background: '#fff' }} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '11px', color: '#555' }}>Cantidad *</label>
                                        <input type="number" min="0" step="any"
                                            value={detalleTemp.cantidad}
                                            onChange={(e) => setDetalleTemp(t => ({ ...t, cantidad: e.target.value }))}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregarLinea(); } }}
                                            style={{ width: '100%', padding: '6px', border: '1px solid #ddd',
                                                     borderRadius: '4px', textAlign: 'right' }} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '11px', color: '#555' }}>Precio ref.</label>
                                        <input type="number" min="0" step="any"
                                            value={detalleTemp.precio}
                                            onChange={(e) => setDetalleTemp(t => ({ ...t, precio: e.target.value }))}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregarLinea(); } }}
                                            style={{ width: '100%', padding: '6px', border: '1px solid #ddd',
                                                     borderRadius: '4px', textAlign: 'right' }} />
                                    </div>
                                    <button type="button" onClick={agregarLinea}
                                        disabled={!detalleTemp.idProducto}
                                        style={{
                                            padding: '7px 10px',
                                            background: detalleTemp.idProducto ? '#004F9F' : '#BBB',
                                            color: 'white', border: 'none', borderRadius: '4px',
                                            cursor: detalleTemp.idProducto ? 'pointer' : 'not-allowed',
                                            fontWeight: '600'
                                        }}>+ Agregar</button>
                                </div>

                                {/* Tabla de lineas ya agregadas */}
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead style={{ background: '#004F9F', color: 'white' }}>
                                        <tr>
                                            <th style={{ padding: '6px', textAlign: 'left' }}>Cod</th>
                                            <th style={{ padding: '6px', textAlign: 'left' }}>Descripcion</th>
                                            <th style={{ padding: '6px', textAlign: 'right' }}>Cantidad *</th>
                                            <th style={{ padding: '6px', textAlign: 'right' }}>Precio ref.</th>
                                            <th style={{ width: '40px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {formulario.detalles.map(d => (
                                            <tr key={d.idProducto} style={{ borderBottom: '1px solid #eee' }}>
                                                <td style={{ padding: '4px' }}>{d.idProducto}</td>
                                                <td style={{ padding: '4px' }}>{d.nomProducto}</td>
                                                <td style={{ padding: '4px', textAlign: 'right' }}>
                                                    <input type="number" min="0" step="any" value={d.cantidad}
                                                        onChange={(e) => actualizarDetalle(d.idProducto, 'cantidad', e.target.value)}
                                                        style={{ width: '90px', padding: '3px 6px', textAlign: 'right',
                                                                border: '1px solid #ddd', borderRadius: '4px' }} />
                                                </td>
                                                <td style={{ padding: '4px', textAlign: 'right' }}>
                                                    <input type="number" min="0" step="any" value={d.precio}
                                                        onChange={(e) => actualizarDetalle(d.idProducto, 'precio', e.target.value)}
                                                        style={{ width: '110px', padding: '3px 6px', textAlign: 'right',
                                                                border: '1px solid #ddd', borderRadius: '4px' }} />
                                                </td>
                                                <td style={{ padding: '4px', textAlign: 'center' }}>
                                                    <button type="button" onClick={() => quitarDetalle(d.idProducto)}
                                                        style={{ background: 'none', border: '1px solid #C62828',
                                                                 color: '#C62828', borderRadius: '4px', padding: '2px 8px' }}>🗑</button>
                                                </td>
                                            </tr>
                                        ))}
                                        {formulario.detalles.length === 0 && (
                                            <tr><td colSpan="5" style={{ padding: '15px', textAlign: 'center', color: '#888' }}>
                                                Sin articulos. Busque arriba, presione + Agregar.
                                            </td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button type="button" onClick={cerrarModal} style={{
                                    padding: '8px 16px', background: '#f5f5f5',
                                    border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer'
                                }}>Cancelar</button>
                                <button type="submit" style={{
                                    padding: '8px 16px', background: '#004F9F',
                                    color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer',
                                    fontWeight: '600'
                                }}>Guardar y sumar stock</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* -------- Modal VER -------- */}
            {modalVerAbierto && recepVer && (
                <div className="atm-modal-overlay" style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)', display: 'flex',
                    justifyContent: 'center', alignItems: 'flex-start', padding: '20px', zIndex: 1000,
                    overflowY: 'auto'
                }}>
                    <div style={{ background: 'white', borderRadius: '8px', padding: '20px', width: '90%', maxWidth: '900px' }}>
                        <h3 style={{ color: '#004F9F', marginTop: 0 }}>
                            Recepcion #{recepVer.nrecep} {chipEstado(recepVer.estado)}
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
                            <div><strong>Fecha:</strong> {formatearFecha(recepVer.fecha)}</div>
                            <div><strong>Deposito:</strong> {recepVer.nom_deposito || recepVer.id_deposito}</div>
                            <div><strong>Proveedor:</strong> {recepVer.nom_proveedor} (RUC {recepVer.ruc_proveedor})</div>
                            <div><strong>OC:</strong> {recepVer.ordencompra || '-'}</div>
                            <div><strong>Remito prov:</strong> {recepVer.remito_prov || '-'} {recepVer.fecha_remito ? `(${formatearFecha(recepVer.fecha_remito)})` : ''}</div>
                            <div><strong>Factura:</strong> {recepVer.ff_nfactura ? `${recepVer.ff_tipo_doc}-${recepVer.ff_nfactura} (Timb ${recepVer.ff_timbrado})` : '-'}</div>
                            <div><strong>Transportista:</strong> {recepVer.nom_transportista || '-'}</div>
                            <div><strong>Chapa:</strong> {recepVer.chapa || '-'} · <strong>Conductor:</strong> {recepVer.nom_conductor || '-'}</div>
                            <div style={{ gridColumn: '1 / span 2' }}><strong>Obs:</strong> {recepVer.obs || '-'}</div>
                        </div>

                        <h4 style={{ marginTop: '15px', color: '#004F9F' }}>Detalle</h4>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ background: '#E4F2FC' }}>
                                <tr>
                                    <th style={{ padding: '6px', textAlign: 'left' }}>#</th>
                                    <th style={{ padding: '6px', textAlign: 'left' }}>Producto</th>
                                    <th style={{ padding: '6px', textAlign: 'left' }}>Descripcion</th>
                                    <th style={{ padding: '6px', textAlign: 'right' }}>Cantidad</th>
                                    <th style={{ padding: '6px', textAlign: 'right' }}>Precio ref.</th>
                                    <th style={{ padding: '6px', textAlign: 'right' }}>Costo ult.compra</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(recepVer.detalles || []).map(d => (
                                    <tr key={d.item} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '4px' }}>{d.item}</td>
                                        <td style={{ padding: '4px' }}>{d.id_producto}</td>
                                        <td style={{ padding: '4px' }}>{d.nom_producto || d.descripcion_producto}</td>
                                        <td style={{ padding: '4px', textAlign: 'right' }}>{formatearNumero(d.cantidad, 3)}</td>
                                        <td style={{ padding: '4px', textAlign: 'right' }}>{formatearNumero(d.precio)}</td>
                                        <td style={{ padding: '4px', textAlign: 'right' }}>{formatearNumero(d.costoulcom)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'flex-end' }}>
                            <button onClick={() => setModalVerAbierto(false)} style={{
                                padding: '8px 16px', background: '#004F9F', color: 'white',
                                border: 'none', borderRadius: '4px', cursor: 'pointer'
                            }}>Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SRecepMateriales;
